/**
 * Service pour la gestion automatique des webhooks Stripe
 * Utilise les Cloud Functions pour vérifier et configurer les webhooks
 */

import { httpsCallable, getFunctions } from 'firebase/functions';

// Types pour les résultats
export interface WebhookCheckResult {
    webhookExists: boolean;
    webhookId?: string;
    webhookUrl?: string;
    isConfiguredCorrectly: boolean;
    missingEvents: string[];
    status?: 'enabled' | 'disabled';
    recommendation: 'ok' | 'create' | 'update';
    message: string;
}

export interface WebhookCreateResult {
    success: boolean;
    webhookId?: string;
    error?: string;
}

export interface AutoConfigureResult {
    action: 'created' | 'updated' | 'none';
    result: WebhookCreateResult;
    checkResult: WebhookCheckResult;
    webhookSecretSaved: boolean;
}

export interface WebhookInfo {
    id: string;
    url: string;
    status: 'enabled' | 'disabled';
    enabledEvents: string[];
    created: number;
    livemode: boolean;
}

export interface ListWebhooksResult {
    webhooks: WebhookInfo[];
    expectedUrl: string;
    requiredEvents: string[];
}

export interface RecreateWebhookResult {
    success: boolean;
    webhookId?: string;
    webhookSecretSaved: boolean;
    error?: string;
}

/**
 * Service pour gérer les webhooks Stripe via Cloud Functions
 */
export class StripeWebhookManagementService {
    private static readonly functions = getFunctions(undefined, 'europe-west1');

    /**
     * Vérifie l'état du webhook pour une organisation
     */
    static async checkWebhookStatus(organizationId: string): Promise<WebhookCheckResult> {
        try {
            const checkFn = httpsCallable<{ organizationId: string }, WebhookCheckResult>(
                this.functions,
                'checkStripeWebhookStatus'
            );

            const result = await checkFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] Check error:', error);
            throw new Error(error.message || 'Erreur lors de la vérification du webhook');
        }
    }

    /**
     * Configure automatiquement le webhook pour une organisation
     */
    static async autoConfigureWebhook(organizationId: string): Promise<AutoConfigureResult> {
        try {
            const configureFn = httpsCallable<{ organizationId: string }, AutoConfigureResult>(
                this.functions,
                'autoConfigureStripeWebhook'
            );

            const result = await configureFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] Configure error:', error);
            throw new Error(error.message || 'Erreur lors de la configuration du webhook');
        }
    }

    /**
     * Liste tous les webhooks configurés
     */
    static async listWebhooks(organizationId: string): Promise<ListWebhooksResult> {
        try {
            const listFn = httpsCallable<{ organizationId: string }, ListWebhooksResult>(
                this.functions,
                'listStripeWebhooks'
            );

            const result = await listFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] List error:', error);
            throw new Error(error.message || 'Erreur lors de la récupération des webhooks');
        }
    }

    /**
     * Retourne les événements requis pour les webhooks CashLive
     */
    static getRequiredEvents(): string[] {
        return [
            'payment_intent.succeeded',
            'payment_intent.payment_failed',
            'payment_intent.canceled',
            'charge.refunded',
            'charge.dispute.created',
            'charge.dispute.closed',
        ];
    }

    /**
     * Envoie un événement de test au webhook pour vérifier la configuration
     */
    static async testWebhook(organizationId: string): Promise<{
        success: boolean;
        message: string;
        paymentIntentId?: string;
        error?: string;
    }> {
        try {
            const testFn = httpsCallable<{ organizationId: string }, {
                success: boolean;
                message: string;
                paymentIntentId?: string;
                error?: string;
            }>(
                this.functions,
                'sendTestWebhookEvent'
            );

            const result = await testFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] Test webhook error:', error);
            throw new Error(error.message || 'Erreur lors du test du webhook');
        }
    }

    /**
     * Vérifie si l'événement de test a été reçu
     */
    static async checkWebhookTestStatus(organizationId: string): Promise<{
        received: boolean;
        lastReceivedAt?: string;
        eventId?: string;
    }> {
        try {
            const checkFn = httpsCallable<{ organizationId: string }, {
                received: boolean;
                lastReceivedAt?: string;
                eventId?: string;
            }>(
                this.functions,
                'checkWebhookTestStatus'
            );

            const result = await checkFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] Check test status error:', error);
            throw new Error(error.message || 'Erreur lors de la vérification du statut du test');
        }
    }

    /**
     * Force la recréation du webhook (supprime l'ancien et recrée un nouveau).
     */
    static async recreateWebhook(organizationId: string): Promise<RecreateWebhookResult> {
        try {
            const recreateFn = httpsCallable<{ organizationId: string }, RecreateWebhookResult>(
                this.functions,
                'recreateStripeWebhook'
            );

            const result = await recreateFn({ organizationId });
            return result.data;
        } catch (error: any) {
            console.error('[StripeWebhookManagement] Recreate webhook error:', error);
            throw new Error(error.message || 'Erreur lors de la recréation du webhook');
        }
    }
}
